import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container,
  Card,
  CardContent,
  Menu,
  MenuItem,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  AccountCircle,
  AdminPanelSettings,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { UserManagement } from './UserManagement';
import { SimpleUserForm } from './SimpleUserForm';
import { User } from '../../contexts/AuthContext';

export const AdminDashboard: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentView, setCurrentView] = useState('dashboard');
  const [userFormOpen, setUserFormOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { user, logout } = useAuth();

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setUserFormOpen(true);
  };

  const handleEditUser = (userToEdit: User) => {
    setSelectedUser(userToEdit);
    setUserFormOpen(true);
  };

  const handleCloseUserForm = () => {
    setUserFormOpen(false);
    setSelectedUser(null);
  };

  const handleUserFormSuccess = () => {
    // Rafraîchir la liste des utilisateurs
    if (currentView === 'users') {
      // Force un re-render du composant UserManagement
      setCurrentView('users');
    }
  };

  const menuItems = [
    { id: 'dashboard', label: 'Tableau de bord', icon: <DashboardIcon /> },
    { id: 'users', label: 'Gestion des utilisateurs', icon: <PeopleIcon /> },
    { id: 'create-user', label: 'Créer un utilisateur', icon: <PersonAddIcon /> },
    { id: 'settings', label: 'Paramètres système', icon: <SettingsIcon /> },
  ];

  const renderContent = () => {
    switch (currentView) {
      case 'dashboard':
        return (
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              Tableau de bord administrateur
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Card sx={{ minWidth: 200, flex: 1 }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Utilisateurs totaux
                  </Typography>
                  <Typography variant="h4">
                    0
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ minWidth: 200, flex: 1 }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Vétérinaires
                  </Typography>
                  <Typography variant="h4">
                    0
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ minWidth: 200, flex: 1 }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Propriétaires
                  </Typography>
                  <Typography variant="h4">
                    0
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ minWidth: 200, flex: 1 }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Animaux
                  </Typography>
                  <Typography variant="h4">
                    0
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Container>
        );
      case 'users':
        return (
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <UserManagement onEditUser={handleEditUser} />
          </Container>
        );
      case 'create-user':
        return (
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" gutterBottom>
                  Créer un utilisateur
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Cliquez sur le bouton ci-dessous pour ouvrir le formulaire de création d'utilisateur.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={handleCreateUser}
                  size="large"
                >
                  Créer un nouvel utilisateur
                </Button>
              </Box>
            </Box>
          </Container>
        );
      case 'settings':
        return (
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              Paramètres système
            </Typography>
            <Typography variant="body1">
              Interface de paramètres système à implémenter...
            </Typography>
          </Container>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar */}
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={() => setSidebarOpen(true)}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <AdminPanelSettings sx={{ mr: 2 }} />
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            VetCare - Administration
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              {user?.first_name} {user?.last_name}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Drawer
        anchor="left"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      >
        <Toolbar />
        <Box sx={{ width: 250 }}>
          <List>
            {menuItems.map((item) => (
              <ListItem
                key={item.id}
                onClick={() => {
                  setCurrentView(item.id);
                  setSidebarOpen(false);
                }}
                sx={{ 
                  cursor: 'pointer',
                  backgroundColor: currentView === item.id ? 'action.selected' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Profile Menu */}
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
      >
        <MenuItem onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 1 }} />
          Déconnexion
        </MenuItem>
      </Menu>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        {renderContent()}
      </Box>

      {/* Formulaire de création/modification d'utilisateur */}
      <SimpleUserForm
        user={selectedUser}
        open={userFormOpen}
        onClose={handleCloseUserForm}
        onSuccess={handleUserFormSuccess}
      />
    </Box>
  );
}; 